import { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import makeStyles from '@material-ui/core/styles/makeStyles'
import Grid from '@material-ui/core/Grid'
import InputLabel from '@material-ui/core/InputLabel'
import OutlinedInput from '@material-ui/core/OutlinedInput'

import { LoadingButton, Subtitle } from '../components'
import { Main, PageHeader } from '../components/Layout'
import { Buttons } from '../components/Steps'
import { FormControl } from '../components/Forms'
import { BirthdateInput, CpfInput } from '../components/Forms/Inputs'

import { constants, validators, studentCpfStorage } from '../utils'
import { appService, subscriptionService } from '../services'
import { cpf as cpfValidator } from 'cpf-cnpj-validator'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
} from '@material-ui/core'

const parseReenrollment = reenrollment => {
  if (!reenrollment) return null

  try {
    const parsed =
      typeof reenrollment === 'string' ? JSON.parse(reenrollment) : reenrollment

    return parsed?.data?.attributes || null
  } catch {
    return null
  }
}

const hasPedagogicalResponsible = enrollment =>
  String(enrollment?.responsibleAcronym || '').trim() !== '' &&
  enrollment?.hasPedagogicalResponsible === true

const useStyles = makeStyles(() => ({
  center: {
    flexDirection: 'column',
    alignItems: 'center',
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    margin: '0 auto',
    width: '100%',
    padding: '30px',
  },
}))

const PreEnrollmentCheck = () => {
  const classes = useStyles()
  const history = useHistory()

  const [cpf, setCpf] = useState('')
  const [birthdate, setBirthdate] = useState('')
  const [openPreEnrollment, setOpenPreEnrollment] = useState(false)
  const [openRenewChallenge, setOpenRenewChallenge] = useState(false)
  const [openRenewEnrollment, setOpenRenewEnrollment] = useState(false)
  const [openStudentFound, setOpenStudentFound] = useState(false)
  const [openFailed, setOpenFailed] = useState(false)
  const [loading, setLoading] = useState(false)
  const [responsibleAcronym, setResponsibleAcronym] = useState('')
  const [challengeCpf, setChallengeCpf] = useState('')
  const [reenrollmentData, setReenrollmentData] = useState(null)
  const [openDataMismatch, setOpenDataMismatch] = useState(false)
  const [openResponsibleMismatch, setOpenResponsibleMismatch] = useState(false)

  const redirectToNewRegistration = () => {
    studentCpfStorage.setStudentCpf(cpf)
    history.push({
      pathname: constants.routes.subscriptions.create.path,
      state: {
        newRegistration: true,
        studentCpf: cpf,
        birthdate,
      },
    })
  }

  const handleReturnToVerification = () => {
    setOpenDataMismatch(false)
  }

  const tryToSearch = (cpf, birthdate) => async () => {
    setLoading(true)

    const { success, payload } =
      await subscriptionService.getSubscriptionAndEnrollment(cpf, birthdate)

    if (success) {
      if (payload?.subscription) {
        setOpenPreEnrollment(true)
      } else if (payload?.reenrollment) {
        const enrollment = parseReenrollment(payload.reenrollment)

        setReenrollmentData(enrollment)
        setResponsibleAcronym(enrollment?.responsibleAcronym || '')
        setChallengeCpf('')
        setOpenRenewEnrollment(true)
      } else if (payload?.student) {
        const studentRecord = parseReenrollment(payload.student)

        setReenrollmentData(studentRecord)
        setResponsibleAcronym(studentRecord?.responsibleAcronym || '')
        setChallengeCpf('')
        setOpenStudentFound(true)
      } else {
        setOpenDataMismatch(true)
      }
    } else {
      setOpenFailed(true)
    }

    setLoading(false)
  }

  const handleCloseRenewChallenge = () => {
    setOpenRenewChallenge(false)
    setChallengeCpf('')
    history.push(constants.routes.home.path)
  }

  const proceedToEnrollmentFlow = () => {
    if (hasPedagogicalResponsible(reenrollmentData)) {
      setOpenRenewChallenge(true)
    } else {
      redirectToNewRegistration()
    }
  }

  const handleProceedFromRenewEnrollment = () => {
    setOpenRenewEnrollment(false)
    proceedToEnrollmentFlow()
  }

  const handleProceedFromStudentFound = () => {
    setOpenStudentFound(false)
    proceedToEnrollmentFlow()
  }

  const handleCloseStudentFound = () => {
    setOpenStudentFound(false)
    history.push(constants.routes.home.path)
  }

  const handleVerifyResponsibleCpf = async () => {
    setLoading(true)

    const { success, payload } = await subscriptionService.verifyResponsible(
      cpf,
      birthdate,
      challengeCpf
    )

    setLoading(false)
    setOpenRenewChallenge(false)
    setChallengeCpf('')

    if (!success) {
      setOpenFailed(true)
    } else if (payload?.matches) {
      redirectToCreateEnrollment(
        parseReenrollment(payload.reenrollment || payload.student)
      )
    } else {
      setOpenResponsibleMismatch(true)
    }
  }

  const handleProceedFromResponsibleMismatch = () => {
    setOpenResponsibleMismatch(false)
    redirectToNewRegistration()
  }

  const handleReturnToResponsibleChallenge = () => {
    setOpenResponsibleMismatch(false)
    setChallengeCpf('')
    setOpenRenewChallenge(true)
  }

  const redirectToCreateEnrollment = enrollment => {
    studentCpfStorage.setStudentCpf(cpf)
    history.push({
      pathname: constants.routes.subscriptions.create.path,
      state: {
        fromReenrollment: true,
        reenrollment: enrollment,
        studentCpf: cpf,
        birthdate,
      },
    })
  }

  const handleClosePreEnrollment = () => {
    setOpenPreEnrollment(false)
    history.push(constants.routes.home.path)
  }

  const handleCloseRenewEnrollment = () => {
    setOpenRenewEnrollment(false)
    history.push(constants.routes.home.path)
  }

  const handleCloseFailed = () => {
    setOpenFailed(false)
  }

  const redirectToPreEnrollment = () =>
    history.push({ pathname: constants.routes.subscriptions.search.path })

  const shouldDisableSearchButton = () =>
    !cpfValidator.isValid(cpf) ||
    !validators.birthdateIsValid(birthdate) ||
    loading

  useEffect(() => {
    if (appService.shouldRedirectToHome('subscribe')) {
      history.push(constants.routes.home.path)
    }
  }, [])

  return (
    <>
      <PageHeader
        buttonText="Voltar"
        title="Verificação CPF"
        onClick={() => history.push(constants.routes.home.path)}
      />

      <Main className={classes.main}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Subtitle>Preencha os campos abaixo com dados do aluno</Subtitle>
          </Grid>

          <Grid item xs={6}>
            <FormControl variant="outlined">
              <InputLabel htmlFor="cpf">CPF do candidato</InputLabel>
              <OutlinedInput
                autoFocus
                disabled={loading}
                label="CPF do candidato"
                variant="outlined"
                id="cpf"
                name="cpf"
                value={cpf}
                onChange={e => setCpf(e.target.value)}
                inputComponent={CpfInput}
              />
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControl variant="outlined">
              <InputLabel htmlFor="birthdate">Data de nascimento</InputLabel>
              <OutlinedInput
                disabled={loading}
                label="Data de nascimento"
                variant="outlined"
                id="birthdate"
                name="birthdate"
                value={birthdate}
                onChange={e => setBirthdate(e.target.value)}
                inputComponent={BirthdateInput}
              />
            </FormControl>
          </Grid>
        </Grid>
      </Main>

      <Buttons extraClasses={classes.center}>
        <LoadingButton
          onClick={async () => {
            tryToSearch(cpf, birthdate)()
          }}
          disabled={shouldDisableSearchButton()}
          loading={loading}
        >
          Verificar
        </LoadingButton>
      </Buttons>

      <Dialog open={openPreEnrollment} onClose={handleClosePreEnrollment}>
        <DialogContent dividers>
          <DialogContentText>
            {
              'Foi identificada uma solicitação de Pré-matrícula para este mesmo CPF.'
            }
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClosePreEnrollment} disabled={loading}>
            Desistir
          </Button>

          <Button color="primary" onClick={redirectToPreEnrollment}>
            Clique aqui para consultar esta inscrição
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openRenewEnrollment} onClose={handleCloseRenewEnrollment}>
        <DialogContent dividers>
          <DialogContentText>
            {
              'Foi identificada uma solicitação de Rematrícula para este CPF. Declaro que estou ciente que, caso prossiga com a pré-matrícula, a rematrícula será cancelada.'
            }
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseRenewEnrollment} disabled={loading}>
            Desistir
          </Button>

          <Button color="primary" onClick={handleProceedFromRenewEnrollment}>
            Prosseguir
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openStudentFound} onClose={handleCloseStudentFound}>
        <DialogContent dividers>
          <DialogContentText>
            {
              'Encontramos os dados cadastrais do aluno informado. Deseja prosseguir com a pré-matrícula utilizando esses dados?'
            }
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseStudentFound} disabled={loading}>
            Desistir
          </Button>

          <Button color="primary" onClick={handleProceedFromStudentFound}>
            Prosseguir
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openRenewChallenge} onClose={handleCloseRenewChallenge}>
        <DialogContent dividers>
          <DialogContentText>
            {`Por favor, informe o CPF do responsável ${responsibleAcronym}.`}
          </DialogContentText>

          <FormControl
            variant="outlined"
            style={{ marginTop: 16, width: '100%' }}
          >
            <InputLabel htmlFor="challengeCpf">CPF do responsável</InputLabel>
            <OutlinedInput
              autoFocus
              disabled={loading}
              label="CPF do responsável"
              variant="outlined"
              id="challengeCpf"
              name="challengeCpf"
              value={challengeCpf}
              onChange={e => setChallengeCpf(e.target.value)}
              inputComponent={CpfInput}
            />
          </FormControl>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseRenewChallenge} disabled={loading}>
            Desistir
          </Button>

          <Button
            color="primary"
            onClick={handleVerifyResponsibleCpf}
            disabled={!cpfValidator.isValid(challengeCpf) || loading}
          >
            Verificar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openResponsibleMismatch}
        onClose={handleReturnToResponsibleChallenge}
      >
        <DialogContent dividers>
          <DialogContentText>
            {
              'Dados do responsável não conferem. Você pode voltar e corrigir o CPF do responsável, ou prosseguir para a inscrição do aluno sem preenchimento dos dados.'
            }
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleReturnToResponsibleChallenge}
            disabled={loading}
          >
            Voltar
          </Button>

          <Button
            color="primary"
            onClick={handleProceedFromResponsibleMismatch}
          >
            Continuar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDataMismatch} onClose={handleReturnToVerification}>
        <DialogContent dividers>
          <DialogContentText>
            {
              'Não identificamos uma pessoa com os dados informados. Deseja retornar para confirmar os dados, ou realizar o cadastro de uma nova pessoa?'
            }
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleReturnToVerification} disabled={loading}>
            Retornar
          </Button>

          <Button
            color="primary"
            onClick={redirectToNewRegistration}
            disabled={loading}
          >
            Novo cadastro
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openFailed} onClose={handleCloseFailed}>
        <DialogContent dividers>
          <DialogContentText>
            {
              'Não foi possível concluir a solicitação no momento. Tente novamente e, caso o problema persista, entre em contato com a SRE de sua região.'
            }
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseFailed} disabled={loading}>
            Fechar e tentar novamente
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default PreEnrollmentCheck
